import { useEffect, useState } from 'react';

import lang from '../../../../../lib/language';
import { RatiosPeriodType } from '../../../../../lib/models/fundamental/types';

import ArrowBack from '../../../../../assets/img/blue_icon-arrow-left.svg';
import { RatiosPeriodButtons } from '../../../../../components';
import { useIsMobile } from '../../../../../hooks';
import Button from '../../../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../../../shared_components/Button/Button.types';
import FilterDropDown from '../FilterDropDown/FilterDropDown';
import TimeOptionsModal from '../Modals/TimeOptionsModal';

import styles from './RatiosComponent.module.scss';

type Props = {
    title: string;
    currentPeriod: string;
    onPeriodChange: (period: RatiosPeriodType) => void;
    currentTimeOption: string;
    timeOptions: string[];
    onTimeChange: (index: number) => void;
    data: Array<Array<string | number>>;
    isLoading: boolean;
}

const RatiosComponent = (props: Props) => {
  const { title, currentPeriod, onPeriodChange, currentTimeOption, timeOptions, onTimeChange, data, isLoading } = props;
  const { isMobile } = useIsMobile();

  const [showMore, setShowMore] = useState(false);
  const [viewAll, setViewAll] = useState(false);

  useEffect(() => {
    if (!isMobile) {
      setViewAll(false);
    }
  }, [isMobile]);

  const renderRows = (rows: Array<Array<string | number>>, showMoreRows: boolean, isViewAll?: boolean) => (
    <div className={isViewAll ? styles.viewAllData : styles.rows}>
      <div className={styles.titleRow}>
        <div className={isMobile ? styles.keyMobile : styles.key} />
        <div className={isMobile ? styles.valuesMobile : styles.values}>
          <div className={isMobile ? styles.valueCompanyMobile : styles.valueCompany}>
            {lang.ratiosCompany()}
          </div>
          <div className={isMobile ? styles.valueSectorMobile : styles.valueSector}>
            {lang.ratiosSector()}
          </div>
        </div>
      </div>
      {rows[0].slice(0, showMoreRows ? rows[0].length : 4).map((name, index) => (
        <div key={name} className={isMobile ? styles.rowMobile : styles.row}>
          <div className={isMobile ? styles.keyMobile : styles.key}>
            {isLoading ? (
              <div className={styles.loadingLine} />
            ) : (
              name
            )}
          </div>
          <div className={styles.values}>
            <div className={styles.value}>
              {isLoading ? (
                <div className={styles.loadingLine} />
              ) : (
                rows[1][index]
              )}
            </div>
            <div className={styles.value}>
              {isLoading ? (
                <div className={styles.loadingLine} />
              ) : (
                rows[2][index]
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      { viewAll ? (
        <div className={styles.viewAllWrapper}>
          <div className={styles.header}>
            <button type="button" className={styles.returnButton} onClick={() => setViewAll(false)}>
              <img src={ArrowBack} alt="Back" />
            </button>
          </div>
          <div className={styles.title}>
            {title}
          </div>
          <div className={styles.periodButtonsWrapper}>
            <RatiosPeriodButtons currentPeriod={currentPeriod} setPeriod={onPeriodChange} />
          </div>
          <div className={styles.timeOptionswrapper}>
            <div className={styles.hiddenMargin} />
            <div className={styles.selector}>
              <TimeOptionsModal
                options={timeOptions}
                currentOption={currentTimeOption}
                onTimeChange={onTimeChange}
                isDisabled={currentPeriod === RatiosPeriodType.TTM}
                currentPeriod={currentPeriod}
              />
            </div>
            <div className={styles.hiddenMargin} />
            <div className={styles.dataContaineMobile}>
              { renderRows(data, true, true) }
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.ratiosComponentWrapper}>
          {data.length > 0 && (
          <>
            <div className={styles.titleAndSelectors}>
              <div className={styles.title}>{title}</div>
              <div className={styles.selectors}>
                { isMobile ? (
                  <Button
                    variant={ButtonTypesEnum.Link}
                    className={styles.viewAll}
                    onClick={() => setViewAll(true)}
                  >
                    {lang.commonViewAll()}
                  </Button>
                ) : (
                  <>
                    <div className={styles.selector}>
                      <FilterDropDown
                        options={Object.values(RatiosPeriodType)}
                        currentOption={currentPeriod}
                        onPeriodChange={(period) => { onPeriodChange(period as RatiosPeriodType); }}
                        hasBackground
                        width={{
                          [RatiosPeriodType.QUARTERLY]: 107,
                          [RatiosPeriodType.ANNUAL]: 97,
                        }[currentPeriod] || 82}
                      />
                    </div>
                    <div className={styles.selector}>
                      <FilterDropDown
                        options={timeOptions}
                        currentOption={currentTimeOption}
                        onTimeChange={onTimeChange}
                        hasBackground
                        disabled={currentPeriod === RatiosPeriodType.TTM}
                        width={85}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className={styles.dataContainer}>
              {isMobile && (<RatiosPeriodButtons currentPeriod={currentPeriod} setPeriod={onPeriodChange} />)}
              { renderRows(data, showMore) }
              {!isMobile && (
                <div className={styles.showMoreButtonContainer}>
                  <Button
                    className={styles.showMoreButton}
                    variant={ButtonTypesEnum.Link}
                    onClick={() => {
                      setShowMore(!showMore);
                    }}
                  >
                    {showMore ? lang.ratiosShowLessButton() : lang.ratiosShowMoreButton()}
                  </Button>
                </div>
              )}
            </div>
          </>
          )}
        </div>
      )}
    </>
  );
};

export default RatiosComponent;
